<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useLoginSection, type LoginSectionProps } from '../useLoginSection'
import { apiPost } from '@/lib/http'

const props = defineProps<LoginSectionProps>()

const emit = defineEmits<{
  (e: 'change', section: string): void
  (e: 'update:shared-data', data: Record<string, unknown>): void
}>()

const { changeSection, setDataItem } = useLoginSection(props, emit)

const fetching = ref(true)
const fetchError = ref<string | null>(null)
const students = ref<Array<{ id: number; first_name: string; last_name: string }>>([])
const selectedStudentIds = ref<number[]>([])

const studentsList = computed(() => {
  return students.value.map(item => ({ text: `${item.last_name} ${item.first_name}`, value: item.id }))
})

function onStudentSelectChange(val: number[] = []) {
  const selected = students.value
    .filter(item => val.includes(item.id))
    .map(({ id, first_name, last_name }) => ({ id, first_name, last_name }))
  setDataItem('smartbookStudents', selected)
}

onMounted(async () => {
  selectedStudentIds.value = (props.sharedData.smartbookStudents || []).map(item => item.id)

  try {
    const resp =
      await apiPost<Array<{ id: number; first_name: string; last_name: string }>>('/parent/request_get_students')
    if (resp.failed) throw resp.message

    students.value = resp.data

    if (students.value.length) {
      setDataItem('parentHasStudents', true)
    } else {
      changeSection(props.LoginSection.StudentCreate)
    }
  } catch (err) {
    let message = 'Произошла неизвестная ошибка.'
    if (typeof err === 'string') message = err
    fetchError.value = message
  }

  fetching.value = false
})
</script>

<template>
  <div class="login-smartbook-student-select full-height">
    <div v-if="fetching" class="login-smartbook-student-select__message blue-text">
      <v-icon class="mdi-spin" size="48">mdi-timer-sand</v-icon>
      <p class="mt-2 text-body-bold">Загрузка...</p>
    </div>

    <div v-else-if="fetchError" class="login-smartbook-student-select__message red-text">
      <v-icon size="48">mdi-alert-circle-outline</v-icon>
      <p class="mt-2 text-body-bold" v-text="fetchError" />
    </div>

    <template v-else>
      <h2 class="text-h3">Выбрать ученика</h2>

      <v-checkbox-group
        v-model="selectedStudentIds"
        class="my-3"
        :items="studentsList"
        @update:model-value="onStudentSelectChange"
      />

      <v-link mode="primary" class="my-2" @click="() => changeSection(LoginSection.StudentCreate)">
        <v-icon class="mr-half">mdi-plus</v-icon>
        Добавить ученика
      </v-link>

      <v-button
        mode="primary filled large block"
        :disabled="!selectedStudentIds.length"
        @click="() => changeSection(LoginSection.SmartbookPurchase)"
      >
        Перейти к оплате
      </v-button>
    </template>
  </div>
</template>

<style lang="scss">
.login-smartbook-student-select {
  display: flex;
  flex-direction: column;
  align-items: center;

  @include desktop {
    width: 320px !important;
    margin: 0 auto;
  }

  &__message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
}
</style>
